import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { ProjectService } from '../../../services/project.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { IProject } from '../../../interfaces';
import { ConfirmPopup, ConfirmPopupModule } from 'primeng/confirmpopup';
import { DirectionService } from '../../../services/direction.service';
import { TooltipModule } from 'primeng/tooltip';
import { SubstringPipe } from '../../../pipes/substring.pipe';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule } from 'primeng/paginator';
import { PaginatorComponent } from '../../paginator/paginator.component';
import { AuthService } from '../../../services/auth.service';
import { authUser } from '../../../interfaces';
import { ReportsService } from '../../../services/reports.service';


@Component({
  selector: 'app-shared-project',
  standalone: true,
  imports: [PaginatorComponent, RouterModule, ConfirmPopupModule, TooltipModule, ButtonModule, SubstringPipe, TranslateModule, TagModule, DatePipe, CommonModule],
  templateUrl: './shared-project.component.html',
  styleUrl: './shared-project.component.scss',
  providers: [ConfirmationService]
})
export class SharedProjectComponent implements OnInit, OnDestroy {
  @ViewChild(ConfirmPopup) confirmPopup!: ConfirmPopup;
  unsubscribe$ = new Subject<void>();
  dir!: 'rtl' | 'ltr';
  user: authUser | null = null;
  constructor(
    private directionService: DirectionService, 
    private projectService: ProjectService, 
    private confirmationService: ConfirmationService, 
    private toastr: ToastrService,
    private authService: AuthService,
    private reportsService: ReportsService
  ) {


  }
  ngOnInit(): void {
    this.directionService.direction$.pipe(takeUntil(this.unsubscribe$)).subscribe((dir) => {
      this.dir = dir;
    })
    this.authService.authBufferSub$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      this.user = user;
    })
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  filteredProjects = [] as IProject[];
  _projects = [] as IProject[];
  @Input() set projects(projects: IProject[]) {
    this._projects = projects;
    this.filteredProjects = structuredClone(this._projects);
  }
  get projects() {
    return this._projects;
  }



  @Input() strategyId!: any;
  hoveredProjectId = 0;
  @Input() isDeleteAllowed = false;
  @Input() isClickable: boolean = true;
  activeMenuId: number | null = null;
  
  onMouseEnter(hoveredProjectId: number) {
    this.hoveredProjectId = hoveredProjectId;
  }
  onMouseLeave() {
    this.hoveredProjectId = 0;
  }
  
  toggleMenu(event: Event, projectId: number): void {
    event.stopPropagation();
    this.activeMenuId = this.activeMenuId === projectId ? null : projectId;
  }
  
  closeMenu(): void {
    this.activeMenuId = null;
  }
  
  closeAllMenus(): void {
    this.activeMenuId = null;
  }

  confirmProjectDelete(event: Event, projectId: number) {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    this.confirmationService.confirm({
      target: target,
      message: this.dir == 'rtl' ? 'هل تريد تأكيد المسح؟' : 'Do you want to delete this project?',
      accept: () => {
        this.projectService.deleteProject(+this.strategyId, projectId).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
          this.projects = this.projects.filter(project => project.id != projectId);
          this.filteredProjects = this.filteredProjects.filter(project => project.id != projectId);
          this.toastr.success(this.dir == 'rtl' ? 'تمت المسح ' : 'The Project has been deleted successfully.', this.dir == 'rtl' ? 'عملية ناجحة' : 'Success');
        })
      },
      reject: () => {
        this.toastr.info(this.dir == 'rtl' ? "تم الالغاء" : "The deletion has been canceled.", this.dir == 'rtl' ? 'الغاء' : 'Canceled');
      }
    });
  }
  accept() {
    this.confirmPopup.accept();
  }
  reject() {
    this.confirmPopup.reject();
  }

  // Get evaluation color based on score (out of 100)
  getEvaluationColor(score: number): string {
    if (!score) return '#6b7280';  // Gray for no score
    if (score >= 70) return '#0A8C93';  // Teal for excellent
    if (score >= 40) return '#f59e0b';  // Orange for average
    return '#ef4444';  // Red for poor
  }

  // Get circle dash array for SVG progress ring (score out of 100)
  getCircleDashArray(score: number): string {
    const circumference = 2 * Math.PI * 28;  // r=28
    const normalizedScore = Math.min(Math.max(score || 0, 0), 100);  // Clamp between 0-100
    const dashOffset = circumference - (normalizedScore / 100) * circumference;
    return `${circumference} ${circumference}`;
  }

  downloadProjectReport(event: Event, projectId: number) {
    event.stopPropagation();
    const locale = this.dir === 'rtl' ? 'ar' : 'en';
    
    this.reportsService.downloadSingleProjectReport(this.strategyId, projectId, locale)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = '';
          anchor.click();
          window.URL.revokeObjectURL(url);
          this.toastr.success(
            this.dir === 'rtl' ? 'تم تحميل التقرير بنجاح' : 'Report downloaded successfully',
            this.dir === 'rtl' ? 'نجاح' : 'Success'
          );
        },
        error: (err) => {
          this.toastr.error(
            this.dir === 'rtl' ? 'حدث خطأ أثناء تحميل التقرير' : 'Error downloading report',
            this.dir === 'rtl' ? 'خطأ' : 'Error'
          );
        }
      });
  }

}
